import { Box, InputAdornment, TextField, TextFieldProps } from '@mui/material'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { InputHeader } from '../Headers/styles'
import RequiredAsterisk from '../RequiredAsterisk/RequiredAsterisk'
import { NumberFormatCustom, NumberFormatCustomProps } from './NumberFormatCustom'

type FormInputProps = TextFieldProps & {
  name: string
  label: string
  unit?: string
  numberFormatProps?: NumberFormatCustomProps
  actionLabel?: React.ReactNode
  maxLength?: number
  maxValue?: number
  inputPattern?: RegExp
}

const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  unit,
  numberFormatProps,
  actionLabel,
  maxLength,
  maxValue,
  inputPattern,
  ...props
}) => {
  const { control, setValue } = useFormContext()

  return (
    <Box mb={2}>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <InputHeader gutterBottom>
          <Box component='span' display='inline-flex' alignItems='baseline'>
            {label}
            <Box component='span' visibility={props.required ? 'visible' : 'hidden'} ml={0.5}>
              <RequiredAsterisk />
            </Box>
          </Box>
        </InputHeader>
        {actionLabel && actionLabel}
      </Box>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...props}
            {...field}
            error={!!error}
            value={field.value}
            helperText={error?.message}
            variant='outlined'
            fullWidth
            disabled={props.disabled}
            sx={{
              ...(props.sx || {}),
              '&.MuiInputBase-root.Mui-disabled': {
                backgroundColor: (theme) => theme.palette.grey[100],
                color: (theme) => theme.palette.text.disabled,
              },
              '&.MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: (theme) => theme.palette.text.disabled,
              },
              '& .MuiInputBase-root': {
                backgroundColor: '#FFFFFF',
              },
            }}
            onChange={(e) => {
              let processedValue = e.target.value

              if (inputPattern) {
                processedValue = e.target.value.replace(inputPattern, '')
                e.target.value = processedValue
                setValue(name, processedValue)
              }

              field.onChange(e)
              if (props.onChange) {
                props.onChange(e)
              }
            }}
            {...(props.type === 'number'
              ? {
                  InputProps: {
                    inputComponent: NumberFormatCustom,
                    endAdornment: unit ? (
                      <InputAdornment position='end'>{unit}</InputAdornment>
                    ) : null,
                    inputProps: {
                      ...numberFormatProps,
                      ...(typeof maxLength !== 'undefined' ? { maxLength } : {}),
                      ...(typeof maxValue !== 'undefined' ? { maxValue } : {}),
                      name: field.name,
                      disabled: props.disabled,
                      onChange: field.onChange,
                      sx: {
                        pt: '10px',
                        pb: '10px',
                      },
                    },
                  },
                }
              : {
                  inputProps: {
                    ...(typeof maxLength !== 'undefined' ? { maxLength } : {}),
                    name: field.name,
                    disabled: props.disabled,
                    onChange: field.onChange,
                    sx: {
                      pt: '10px',
                      pb: '10px',
                    },
                  },
                  InputProps: {
                    endAdornment: unit ? (
                      <InputAdornment position='end'>{unit}</InputAdornment>
                    ) : null,
                  },
                })}
            type={'text'}
          />
        )}
      />
    </Box>
  )
}

export default FormInput
